import { useEffect, useState } from 'react';

import { Divider, HStack, Text, VStack } from '@chakra-ui/react';
import { Sheet } from 'react-modal-sheet';
import { useNavigate } from 'react-router-dom';
import { ArrowRightSmall, ChevronDownSmall } from '@peacecover/icon';
import { Button } from '@peacecover/component';

import { t } from 'i18next';
import {
  canContinueWithNewSocialAccount,
  getSocialCandidateEmails,
  getSocialLoginErrorMessage,
  getSocialSessionToken,
  isSocialLoginSuccess,
  shouldRequireEmailVerification,
  socialLogin
} from '../../data/repositories/SocialLogin';
import { completeAuthLogin } from '@pocapan/shared/utils/completeAuthLogin';
import { getUserLocale } from '@pocapan/shared/utils/getUserLocale';
import { shouldShowSocialLoginMethods } from '@pocapan/shared/utils/NativeUtil';
import {
  getOnboardingSocialCredential,
  tryStartNativeOAuthRedirect
} from '@pocapan/shared/utils/socialLoginClientFlow';
import PriamryCheckBox from '../checkbox/PriamryCheckBox';
import GradientLoadingSpinner from '../../presentation/components/GradientLoadingSpinner';

function getProviderButtonAppearance(provider) {
  switch (provider) {
    case 'kakao':
      return {
        className: 'border border-[#F9E001] bg-[#F9E001] text-main-light-2 text-nowrap'
      };
    case 'facebook':
      return {
        className: 'border border-[#1877F2] bg-[#1877F2] text-white text-nowrap'
      };
    case 'google':
    case 'apple':
      return {
        className: 'border border-[#E7E7E7] bg-[#FFF] text-main-light-2 text-nowrap'
      };
    case 'email':
      return {
        className:
          'border border-secondary-light dark:border-secondary-dark bg-background-light dark:bg-background-dark text-main-light-1 dark:text-main-dark-1'
      };
    default:
      return {
        className: 'border border-[#E7E7E7] bg-[#FFF] text-main-light-2 text-nowrap'
      };
  }
}

function ProviderBrandIcon({ provider }) {
  if (provider === 'google') {
    return <img src="/assets/icons/login/google.png" alt="Google" className="w-[24px] h-[24px]" />;
  }

  if (provider === 'kakao') {
    return (
      <img src="/assets/icons/login/kakaotalk.png" alt="Kakao" className="w-[24px] h-[24px]" />
    );
  }

  if (provider === 'apple') {
    return <img src="/assets/icons/login/apple.png" alt="Apple" className="w-[24px] h-[24px]" />;
  }

  if (provider === 'facebook') {
    return (
      <img src="/assets/icons/login/facebook.png" alt="Facebook" className="w-[24px] h-[24px]" />
    );
  }

  if (provider === 'email') {
    return <span className="font-black text-[13px] leading-none">@</span>;
  }

  return null;
}

function getProviderButtonText(provider) {
  switch (provider) {
    case 'google':
      return t('BaseAgreementBottomSheet.loginWithGoogle');
    case 'facebook':
      return t('BaseAgreementBottomSheet.loginWithFacebook');
    case 'kakao':
      return t('BaseAgreementBottomSheet.loginWithKakao');
    case 'apple':
      return t('BaseAgreementBottomSheet.loginWithApple');
    default:
      return;
  }
} 

/**
 * @param {{
 *   isOpen: boolean;
 *   onAgreementClick: (isChild: boolean, isPromotionAgreed: boolean) => void;
 *   title?: any;
 *   description?: any;
 *   buttonText?: any;
 *   transparentBackground?: boolean;
 *   showLoginMethods?: boolean;
 *   prefetchedSocialMethods?: Array<{ provider: string; display_name: string }>;
 *   isSocialMethodsPrefetching?: boolean;
 *   socialMethodsPrefetchError?: any;
 * }} props
 */
export default function BaseAgreementBottomSheet({
  isOpen,
  onAgreementClick,
  title,
  description,
  buttonText = t('introPage.startButton'),
  transparentBackground = false,
  showLoginMethods = false,
  prefetchedSocialMethods = [],
  isSocialMethodsPrefetching = false,
  socialMethodsPrefetchError = ''
}) {
  const [isAgreeAll, setIsAgreeAll] = useState(false);
  const [isNotChild, setIsNotChild] = useState(false);
  const [termAndPrivacy, setTermAndPrivacy] = useState(false);
  const [marketingAgreement, setMarketingAgreement] = useState(false);
  const [socialErrorMessage, setSocialErrorMessage] = useState(null);
  const [socialLoadingKey, setSocialLoadingKey] = useState(null);
  const [isLoginMethodsExpanded, setIsLoginMethodsExpanded] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if all child checkboxes are checked
    const allChecked = isNotChild && termAndPrivacy && marketingAgreement;

    // Set the state of the parent checkbox accordingly
    setIsAgreeAll(allChecked);
  }, [isNotChild, termAndPrivacy, marketingAgreement]);

  const isRequiredAllChecked = () => {
    return termAndPrivacy && isNotChild;
  };

  const onClickButton = () => {
    if (!isRequiredAllChecked()) {
      return;
    }
    onAgreementClick(!isNotChild, marketingAgreement);
  };

  const handleAgreeAllToggle = (checked) => {
    setIsAgreeAll(checked);
    setIsNotChild(checked);
    setTermAndPrivacy(checked);
    setMarketingAgreement(checked);
  };

  useEffect(() => {
    if (isOpen) return;
    setSocialErrorMessage(null);
    setSocialLoadingKey(null);
    setIsLoginMethodsExpanded(false);
  }, [isOpen]);

  const finalizeSocialLogin = (response) => {
    const completed = completeAuthLogin({
      response,
      navigate
    });
    if (!completed) {
      setSocialErrorMessage(t('requestVerificationCodePage.socialLoginFailedMessage'));
    }
  };

  const handleEmailLogin = () => {
    if (!isRequiredAllChecked()) return;
    onAgreementClick(!isNotChild, marketingAgreement);
  };

  const handleSocialLogin = async (provider) => {
    if (!isRequiredAllChecked()) return;

    setSocialErrorMessage(null);
    setSocialLoadingKey(provider);

    try {
      if (provider === 'google') {
        const oauth = tryStartNativeOAuthRedirect('google', marketingAgreement);
        if (oauth.status === 'started') {
          setSocialLoadingKey(null);
          return;
        }
      }

      if (provider === 'facebook') {
        const oauth = tryStartNativeOAuthRedirect('facebook', marketingAgreement);
        if (oauth.status === 'started') {
          setSocialLoadingKey(null);
          return;
        }
        if (oauth.reason === 'no_url') {
          setSocialErrorMessage(t('requestVerificationCodePage.socialLoginUnavailableMessage'));
          return;
        }
      }

      const credential = await getOnboardingSocialCredential(provider);

      if (!credential) {
        if (provider === 'apple') {
          return;
        }
        setSocialErrorMessage(t('requestVerificationCodePage.socialLoginUnavailableMessage'));
        return;
      }

      const socialResponse = await socialLogin({
        provider,
        ...credential,
        isPromotionAgreed: marketingAgreement
      });

      if (isSocialLoginSuccess(socialResponse)) {
        finalizeSocialLogin(socialResponse);
        return;
      }

      if (shouldRequireEmailVerification(socialResponse)) {
        const socialSessionToken = getSocialSessionToken(socialResponse);
        const candidateEmails = getSocialCandidateEmails(socialResponse);
        if (!socialSessionToken || candidateEmails.length === 0) {
          setSocialErrorMessage(
            t('requestVerificationCodePage.socialCandidateEmailsMissingMessage')
          );
          return;
        }

        navigate('/social-account-selection?init=true', {
          state: {
            provider,
            socialSessionToken,
            candidateEmails,
            continueWithNewAccountAvailable: canContinueWithNewSocialAccount(socialResponse),
            isPromotionAgreed: marketingAgreement
          }
        });
        return;
      }

      const errorCode = socialResponse?.error?.code;
      if (errorCode === 'PROVIDER_ALREADY_LINKED') {
        setSocialErrorMessage(t('requestVerificationCodePage.socialProviderAlreadyLinkedMessage'));
        return;
      }

      setSocialErrorMessage(
        getSocialLoginErrorMessage(socialResponse) ||
          t('requestVerificationCodePage.socialLoginFailedMessage')
      );
    } catch (_error) {
      setSocialErrorMessage(t('requestVerificationCodePage.socialLoginFailedMessage'));
    } finally {
      setSocialLoadingKey(null);
    }
  };

  const resolvedSocialMethods = Array.isArray(prefetchedSocialMethods)
    ? prefetchedSocialMethods
    : [];
  const canShowLoginMethods = showLoginMethods && shouldShowSocialLoginMethods();
  const shouldShowSocialMethodsLoading = canShowLoginMethods && isSocialMethodsPrefetching;
  const primaryLoginMethod = resolvedSocialMethods.length > 0 ? resolvedSocialMethods[0] : null;
  const collapsedLoginMethods =
    resolvedSocialMethods.length > 0 ? [...resolvedSocialMethods.slice(1)] : [];
  const shouldShowCollapsedMethods = collapsedLoginMethods.length > 0;
  const isLoginButtonDisabled = !isRequiredAllChecked() || socialLoadingKey !== null;
  const displaySocialErrorMessage = socialErrorMessage || socialMethodsPrefetchError || null;

  const onClickLoginMethod = (provider) => {
    if (provider === 'email') {
      handleEmailLogin();
      return;
    }
    handleSocialLogin(provider);
  };

  const renderLoginButton = (method) => {
    if (!method) return null;

    const isCurrentProviderLoading = socialLoadingKey === method.provider;
    const appearance = getProviderButtonAppearance(method.provider);
    const buttonText = getProviderButtonText(method.provider);
    const baseClassName = `
        flex items-center justify-start relative
        rounded-full
        min-h-[52px] pl-[17px]
        font-semibold text-[17px] leading-[20px]
        disabled:opacity-50 disabled:cursor-not-allowed
      `;

    return (
      <button
        key={method.provider}
        type="button"
        onClick={() => onClickLoginMethod(method.provider)}
        disabled={isLoginButtonDisabled}
        className={`${baseClassName} ${appearance.className}`}
        style={appearance.style}
        aria-label={`${method.display_name} login`}
      >
        {isCurrentProviderLoading
          ? <div className="flex items-center justify-center w-full h-full">
              <GradientLoadingSpinner size={24} />
            </div>
          : <>
              <ProviderBrandIcon provider={method.provider} />
              <span className="absolute left-1/2 -translate-x-1/2">{buttonText}</span>
            </>}
      </button>
    );
  };

  return (
    <div className="h-[120px]">
      <Sheet isOpen={isOpen} disableDrag={true} onClose={() => {}} detent="content">
        <Sheet.Container
          className="!bg-background-elevated"
          style={{
            paddingLeft: '20px',
            paddingRight: '20px',
            borderTopLeftRadius: '20px',
            borderTopRightRadius: '20px',
            boxShadow: '0px -6px 54px 0px rgba(0,0,0,0.13)'
          }}
        >
          <Sheet.Header className="pt-[19px] pb-[16px]">
            {title && (
              <Text
                fontWeight={600}
                fontSize={'20px'}
                marginTop={'10px'}
                paddingRight={'36px'}
                wordBreak={'auto-phrase'}
              >
                {t('agreements.baseAgreementBottomSheetTitle')}
              </Text>
            )}
            {description && (
              <Text
                fontWeight={400}
                fontSize={'14px'}
                marginTop={'7px'}
                marginBottom={'20px'}
                paddingRight={'36px'}
                wordBreak={'auto-phrase'}
              >
                {description}
              </Text>
            )}
            <button
              type="button"
              onClick={() => handleAgreeAllToggle(!isAgreeAll)}
              disabled={socialLoadingKey !== null}
              className="flex items-center gap-[8px]"
            >
              <PriamryCheckBox size={'lg'} isChecked={isAgreeAll} />
              <p className="text-text-main-lv1 text-[17px] font-semibold">
                {t('agreements.baseAgreementBottomAgreeAllButton')}
              </p>
            </button>
            <Divider className="py-[7px]" />
          </Sheet.Header>
          <Sheet.Content className="py-1" disableDrag={true}>
            <VStack align={'flex-start'} spacing={2.5}>
              <button
                type="button"
                onClick={() => setIsNotChild(!isNotChild)}
                disabled={socialLoadingKey !== null}
                className="flex items-center gap-[8px]"
              >
                <PriamryCheckBox isChecked={isNotChild} />
                <p className="text-[15px] text-text-main-lv3">
                  {t('agreements.baseAgreementChangeAbove14')}
                </p>
              </button>
              <button
                type="button"
                onClick={() => setTermAndPrivacy(!termAndPrivacy)}
                disabled={socialLoadingKey !== null}
                className="flex items-center gap-[8px]"
              >
                <PriamryCheckBox isChecked={termAndPrivacy} />
                <HStack spacing={0.5}>
                  <p className="text-[15px] text-text-main-lv3">
                    {t('agreements.baseAgreementRequiredCheckBox')}
                  </p>

                  <a
                    href={`/fanhearts?path=terms&category=terms_of_service&locale=${getUserLocale()}`}
                  >
                    <p className="text-[15px] text-text-main-lv3 underline">
                      {t('agreements.baseAgreementRequiredAgreement')}
                    </p>
                  </a>
                </HStack>
              </button>
              <button
                type="button"
                onClick={() => setMarketingAgreement(!marketingAgreement)}
                disabled={socialLoadingKey !== null}
                className="flex items-center gap-[8px]"
              >
                <PriamryCheckBox isChecked={marketingAgreement} />
                <HStack spacing={0.5}>
                  <p className="text-[15px] text-text-main-lv3">
                    {t('agreements.baseAgreementOptionalCheckBox')}
                  </p>
                  <a
                    href={`/fanhearts?path=terms&category=marketing_consent&locale=${getUserLocale()}`}
                  >
                    <p className="text-[15px] text-text-main-lv3 underline">
                      {t('agreements.baseAgreementPromotionalAgreement')}
                    </p>
                  </a>
                </HStack>
              </button>
              {canShowLoginMethods
                ? <div style={{ marginTop: '25px', width: '100%', marginBottom: '4px' }}>
                    {shouldShowSocialMethodsLoading
                      ? <VStack align={'stretch'} spacing={2}>
                          <button
                            type="button"
                            disabled={true}
                            className="
                              flex items-center justify-center gap-[10px]
                              rounded-[56px]
                              min-h-[52px]
                              font-bold text-[16px]
                              border border-secondary-light dark:border-secondary-dark
                              bg-background-light dark:bg-background-dark
                              text-main-light-2 dark:text-main-dark-2
                              opacity-80
                            "
                          >
                            <div
                              className="
                                animate-spin
                                w-5 h-5
                                border-[3px]
                                border-label-light dark:border-label-dark
                                border-t-primary-light dark:border-t-primary-dark
                                rounded-full
                              "
                            />
                            <span>
                              {t('requestVerificationCodePage.socialLoginMethodsLoadingMessage')}
                            </span>
                          </button>
                        </VStack>
                      : <div className="flex flex-col gap-[28px]">
                          {renderLoginButton(primaryLoginMethod)}

                          {shouldShowCollapsedMethods
                            ? <>
                                <button
                                  type="button"
                                  onClick={() => setIsLoginMethodsExpanded((prev) => !prev)}
                                  className="
                                text-text-main-lv2
                                text-[15px] font-semibold leading-[18px]
                                flex items-center justify-center gap-[4px]
                              "
                                  disabled={socialLoadingKey !== null}
                                >
                                  {t('requestVerificationCodePage.socialExpandMethods')}
                                  <ChevronDownSmall
                                    size={20}
                                    className={`text-text-main-lv2 ${isLoginMethodsExpanded ? 'rotate-180' : ''}`}
                                  />
                                </button>

                                <div className="flex flex-col gap-[10px]">
                                  {isLoginMethodsExpanded
                                    ? collapsedLoginMethods.map((method) =>
                                        renderLoginButton(method)
                                      )
                                    : null}
                                </div>
                                {isLoginMethodsExpanded && (
                                  <button
                                    type="button"
                                    className="flex items-center justify-center gap-[4px] mb-[20px] text-[13px] text-text-main-lv3"
                                    onClick={() => onClickLoginMethod('email')}
                                  >
                                    {t('requestVerificationCodePage.emailLoginButton')}
                                    <ArrowRightSmall size={20} />
                                  </button>
                                )}
                              </>
                            : null}

                          {displaySocialErrorMessage
                            ? <Text fontSize={'13px'} color={'red.400'}>
                                {displaySocialErrorMessage}
                              </Text>
                            : null}
                        </div>}
                  </div>
                : <div className="!mt-[25px] w-full !mb-[8px]">
                    <Button
                      size="large"
                      onClick={onClickButton}
                      disabled={isLoginButtonDisabled}
                      className="w-full text-[17px] font-semibold"
                    >
                      {buttonText}
                    </Button>
                  </div>}
            </VStack>
          </Sheet.Content>
        </Sheet.Container>
        <Sheet.Backdrop style={transparentBackground ? { backgroundColor: 'transparent' } : {}} />
      </Sheet>
    </div>
  );
}
